import { Refresh } from "@mui/icons-material";
import { IconButton, InputAdornment, Tooltip } from "@mui/material";
import { ReactNode } from "react";
import { BREAK_GLASS_APPROVED_EVENT, ENDPOINTS } from "config/constants";
import { useContextController } from "context";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BoxModel } from "types";
import { ensureBreakGlassForSite, getBoxLabel, isBreakGlassApproved } from "utils";
import SelectInput from "./SelectInput";
import TextInput from "./TextInput";

interface Data {
  boxes?: BoxModel[];
  totalRecords?: number;
}

interface IBoxSelect {
  value?: string;
  onChange: (boxId?: string) => void;
  hideLabel?: boolean;
  label?: string;
  helperText?: string;
  startAdornment?: ReactNode;
  siteId?: string;
  onBox?: (box?: BoxModel) => void;
}

function siteIdOf(box?: BoxModel | null) {
  return box?.siteId || box?.site?._id || "";
}

export default function BoxSelect({
  value,
  hideLabel,
  label,
  helperText,
  startAdornment,
  onChange,
  siteId,
  onBox
}: IBoxSelect) {
  const [{ userData }] = useContextController();
  const [reload, setReload] = useState(false);
  const [pendingBoxId, setPendingBoxId] = useState<string | null>(null);

  const t = useTranslation();

  const { isLoading, response, error, callFetch: callFetchGet } = useFetch<Data>("GET");

  const boxes = useMemo(() => response?.data?.boxes || [], [response?.data?.boxes]);

  const { enqueueSnackbar } = useSnackbar();

  const onReload = useCallback(() => setReload((prevState) => !prevState), []);

  const trySelectBox = useCallback(
    (boxId?: string) => {
      if (!boxId) {
        onChange(undefined);
        return;
      }
      const box = boxes.find((b) => b._id === boxId);
      const site = siteIdOf(box);
      if (userData?.type === "super" && site && !isBreakGlassApproved(site)) {
        setPendingBoxId(boxId);
        ensureBreakGlassForSite(userData.type, site);
        return;
      }
      setPendingBoxId(null);
      onChange(boxId);
    },
    [boxes, onChange, userData?.type]
  );

  useEffect(() => {
    if (!error) return;
    enqueueSnackbar(error?.message || JSON.stringify(error), {
      variant: "error"
    });
  }, [error, enqueueSnackbar]);

  useEffect(() => {
    const searchParams = new URLSearchParams();
    searchParams.append("limit", "0");
    if (siteId) {
      searchParams.append("siteId", siteId);
    }
    const params = `?${searchParams.toString()}`;
    callFetchGet(ENDPOINTS.boxes + params).catch(console.error);
  }, [callFetchGet, reload, siteId]);

  useEffect(() => {
    const id = localStorage.getItem("boxId");
    if (boxes.some((elm) => elm._id === id + "")) {
      trySelectBox(id + "");
    } else if (boxes[0]?._id) {
      trySelectBox(boxes[0]._id);
    } else {
      onChange(undefined);
    }
    // Intentionally depend on boxes/loading, not trySelectBox identity churn
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, boxes]);

  useEffect(() => {
    const onApproved = (event: Event) => {
      const approvedSite = (event as CustomEvent<{ siteId?: string }>).detail?.siteId;
      if (!pendingBoxId) return;
      const box = boxes.find((b) => b._id === pendingBoxId);
      const site = siteIdOf(box);
      if (approvedSite && site && String(approvedSite) === String(site)) {
        onChange(pendingBoxId);
        setPendingBoxId(null);
      }
    };
    window.addEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved as EventListener);
    return () => window.removeEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved as EventListener);
  }, [pendingBoxId, boxes, onChange]);

  useEffect(() => {
    onBox?.(boxes.find((box) => box._id === value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxes, value]);

  return boxes.length > 0 ? (
    <SelectInput
      id="box-select"
      label={!hideLabel ? label || t["Box"] : undefined}
      helperText={hideLabel ? helperText || "" : helperText}
      startAdornment={startAdornment}
      data={boxes.map((box) => ({
        label: getBoxLabel(box),
        value: box._id
      }))}
      value={value ? value : ""}
      onChange={({ target: { value: next } }) => {
        trySelectBox(next as string);
      }}
      sx={{ minWidth: hideLabel ? 0 : 300 }}
      placeholder="No Boxes Found"
      loading={isLoading}
      fullWidth
    />
  ) : (
    <TextInput
      disabled
      fullWidth
      sx={{ minWidth: hideLabel ? 0 : 300 }}
      label={!hideLabel ? t["Box"] : undefined}
      helperText={hideLabel ? "" : undefined}
      value={isLoading ? "Loading..." : "No Boxes Found"}
      onClick={onReload}
      endAdornment={
        <InputAdornment position="end">
          <Tooltip title="Retry">
            <IconButton onClick={onReload}>
              <Refresh color="secondary" />
            </IconButton>
          </Tooltip>
        </InputAdornment>
      }
    />
  );
}
